import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import type { FormEvent, ReactNode } from 'react'
import { ChevronDown, Search, X } from '../icon'
import { PageMasthead } from '../page-masthead/PageMasthead.tsx'
import { MastheadTab } from '../page-masthead/MastheadTab.tsx'
import styles from './NewsMasthead.module.css'

export interface NewsTopic {
  value: string
  /** Shown text; defaults to `value`. */
  label?: string
  /** Number of articles, shown in the More menu. */
  count?: number
}

export interface NewsMastheadLink {
  label: string
  to: string
  active?: boolean
}

export interface NewsMastheadSearch {
  /** The active search, or null. */
  query: string | null
  onSearch: (query: string | null) => void
  placeholder?: string
}

export interface NewsMastheadProps {
  /** Small line above the title, e.g. the org name. */
  eyebrow?: ReactNode
  title: ReactNode
  /** Topics in display order, most important first. */
  topics?: NewsTopic[]
  /** Selected topic value; null for All. */
  activeTopic?: string | null
  onTopicChange?: (topic: string | null) => void
  /**
   * Most topics shown inline. Fewer show when the row wouldn't fit beside the
   * title: the lowest-ranked fold into More, so the row never wraps. Stacked
   * under the title on a phone, it scrolls sideways instead.
   */
  maxVisible?: number
  allLabel?: string
  /** Section links after the topics, e.g. Media. */
  links?: NewsMastheadLink[]
  /** Adds a search button at the end of the row; it opens a field in place of the tabs. */
  search?: NewsMastheadSearch
  as?: 'h1' | 'h2'
  className?: string
}

/**
 * The top of a news front page: a big display title with the topic tabs set
 * beside it as plain text on one line, the active one underlined.
 */
export function NewsMasthead({
  eyebrow,
  title,
  topics = [],
  activeTopic = null,
  onTopicChange,
  maxVisible = 5,
  allLabel = 'All',
  links = [],
  search,
  as: Heading = 'h1',
  className,
}: NewsMastheadProps) {
  const [open, setOpen] = useState(false)
  const [fit, setFit] = useState(maxVisible)
  const headerRef = useRef<HTMLElement>(null)
  const navRef = useRef<HTMLElement>(null)
  const tabsRef = useRef<HTMLDivElement>(null)
  const moreRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLUListElement>(null)
  const [searching, setSearching] = useState(!!search?.query)
  const [draft, setDraft] = useState(search?.query ?? '')
  const activeQuery = search?.query ?? null

  // A search arriving from the URL (back button, a shared link) opens the field.
  useEffect(() => {
    if (activeQuery === null) return
    setSearching(true)
    setDraft(activeQuery)
  }, [activeQuery])

  const inline = Math.min(maxVisible, fit)
  const visible = topics.slice(0, inline)
  const overflow = topics.slice(inline)
  const activeOverflow = overflow.find((t) => t.value === activeTopic)
  const onLink = links.some((l) => l.active)
  const showNav = topics.length > 0 || links.length > 0 || !!search

  function submitSearch(e: FormEvent) {
    e.preventDefault()
    search!.onSearch(draft.trim() || null)
  }

  function closeSearch() {
    setSearching(false)
    setDraft('')
    if (search!.query) search!.onSearch(null)
  }

  // Start from the most that could show whenever the topics or the space change...
  const topicKey = topics.map((t) => t.value).join('\n')
  useLayoutEffect(() => {
    setFit(maxVisible)
    // Test environments may lack it; without it the row simply doesn't refit on resize.
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(() => setFit(maxVisible))
    observer.observe(headerRef.current!)
    return () => observer.disconnect()
  }, [maxVisible, topicKey])

  // ...then fold one topic at a time, before paint, until the row fits.
  useLayoutEffect(() => {
    const tabs = tabsRef.current
    if (!tabs || fit === 0) return
    const beside = getComputedStyle(headerRef.current!).flexDirection === 'row'
    const nav = navRef.current!
    const crowded = tabs.scrollWidth > tabs.clientWidth || nav.scrollWidth > nav.clientWidth
    if (beside && crowded) setFit(Math.min(fit, topics.length) - 1)
    // Everything that changes the row's width: the folded count, the topics,
    // the More label, the links, and the search field replacing the row.
  }, [fit, topicKey, topics.length, activeTopic, links.length, searching])

  useEffect(() => {
    if (!open) return
    menuRef.current!.querySelector<HTMLButtonElement>('button')!.focus()

    function onPointerDown(e: MouseEvent) {
      if (!moreRef.current!.contains(e.target as Node)) setOpen(false)
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key !== 'Escape') return
      setOpen(false)
      buttonRef.current!.focus()
    }
    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  function select(topic: string | null) {
    setOpen(false)
    onTopicChange?.(topic)
  }

  const aside = searching && search ? (
    <form role="search" className={styles.search} onSubmit={submitSearch}>
      <Search size={16} aria-hidden="true" className={styles.searchIcon} />
      <input
        type="search"
        className={styles.searchInput}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder={search.placeholder ?? 'Search news'}
        aria-label="Search news"
        autoFocus
      />
      <button type="button" className={styles.iconButton} onClick={closeSearch} aria-label="Close search">
        <X size={16} aria-hidden="true" />
      </button>
    </form>
  ) : showNav ? (
    <nav ref={navRef} aria-label="News topics" className={styles.nav}>
      {topics.length > 0 && (
        <div ref={tabsRef} className={styles.tabs}>
          <MastheadTab
            active={activeTopic === null && !onLink}
            aria-pressed={activeTopic === null && !onLink}
            onClick={() => select(null)}
          >
            {allLabel}
          </MastheadTab>
          {visible.map((t) => (
            <MastheadTab
              key={t.value}
              active={t.value === activeTopic}
              aria-pressed={t.value === activeTopic}
              onClick={() => select(t.value)}
            >
              {t.label ?? t.value}
            </MastheadTab>
          ))}
        </div>
      )}

      {overflow.length > 0 && (
        <div className={styles.more} ref={moreRef}>
          <MastheadTab
            ref={buttonRef}
            active={!!activeOverflow}
            aria-haspopup="menu"
            aria-expanded={open}
            onClick={() => setOpen((o) => !o)}
          >
            {activeOverflow ? (activeOverflow.label ?? activeOverflow.value) : 'More'}
            <ChevronDown size={14} aria-hidden="true" className={styles.chevron} />
          </MastheadTab>
          {open && (
            <ul role="menu" className={styles.menu} ref={menuRef}>
              {overflow.map((t) => (
                <li key={t.value} role="none">
                  <button
                    type="button"
                    role="menuitemradio"
                    aria-checked={t.value === activeTopic}
                    className={styles.menuItem}
                    onClick={() => select(t.value)}
                  >
                    <span>{t.label ?? t.value}</span>
                    {t.count !== undefined && <span className={styles.count}>{t.count}</span>}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {links.map((l) => (
        <MastheadTab key={l.to} to={l.to} active={!!l.active}>{l.label}</MastheadTab>
      ))}

      {search && (
        <button type="button" className={styles.iconButton} onClick={() => setSearching(true)} aria-label="Search news">
          <Search size={16} aria-hidden="true" />
        </button>
      )}
    </nav>
  ) : null

  return (
    <PageMasthead ref={headerRef} eyebrow={eyebrow} title={title} as={Heading} className={className} aside={aside} />
  )
}
